import { useEffect, useState } from "react";
import axios from "axios";
import { PAYMENT_TERMS_CONTENT_URL } from "../constants";

/** The policy changes rarely and is the same for everyone. */
let cache: string | null = null;

/**
 * Strips what the page builder leaves behind.
 *
 * WordPress hands back what is stored, not what it renders, so the answer still
 * carries WPBakery shortcodes and the theme's generated `<style>` blocks. Both
 * are noise here — the shortcodes would read as literal `[vc_row]` text, and
 * the styles belong to a page laid out nothing like this dialog.
 *
 * What survives is the prose and its structure: headings, paragraphs and lists.
 * Sanitising happens at the point of rendering, not here.
 */
function stripPageBuilderMarkup(html: string): string {
  return html
    .replace(/<style[\s\S]*?<\/style>/gi, "")
    .replace(/<script[\s\S]*?<\/script>/gi, "")
    .replace(/\[[^\]]*\]/g, "");
}

/**
 * Loads the payment policy from the marketing site.
 *
 * Returns null rather than throwing when anything is off — a missing page, a
 * network failure, an answer that no longer matches the expected page. The
 * dialog falls back to linking out, which is where this copy lived before, so a
 * failure here costs the reader nothing.
 */
export default function usePaymentTermsContent() {
  const [html, setHtml] = useState<string | null>(cache);
  const [loading, setLoading] = useState(cache === null);

  useEffect(() => {
    if (cache !== null) return;

    let active = true;
    (async () => {
      try {
        setLoading(true);
        const { data } = await axios.get(PAYMENT_TERMS_CONTENT_URL);
        const page =
          Array.isArray(data) && data.length > 0 ? data[0] : undefined;
        const content = stripPageBuilderMarkup(page?.content?.rendered ?? "");
        // An empty body is as useless as a failed request, and both end the
        // same way: the link stands in for the text.
        if (content.trim().length > 0) cache = content;
        if (active) setHtml(content.trim().length > 0 ? content : null);
      } catch {
        if (active) setHtml(null);
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, []);

  return { html, loading };
}
